import type {
  SalonStockerHttpTypes,
  StoreReorderLine,
  StoreStockReorderLine
} from "@repo/api-sdk";

/**
 * The **reorder-diff** module: the single owner of the storefront's
 * *changed-line diff* domain logic (ADR-0002). A reorder (F2) and a saved list
 * (F3) both resolve their lines against current seller offers; either source is
 * normalized into one `ReorderLine` vocabulary here, and this module owns the
 * classification, the addable tally, and the unavailable-reason labels every
 * reorder surface shares.
 *
 * Callers normalize once at the boundary (`normalizeReorderLines` /
 * `normalizeResolvedItems`) and then speak only `ReorderLine`. Adding a new
 * reorder surface means consuming this interface, never re-deriving the rules.
 */

/** The addable axis of a resolved line. */
export type ReorderLineStatus = "available" | "repriced" | "unavailable";

/** Why an `unavailable` line cannot be re-added (meaningful only then). */
export type UnavailableReason = "discontinued" | "out_of_stock" | "inactive";

/**
 * A resolved reorder line in the storefront's own vocabulary - the canonical
 * shape both F2 (reorder) and F3 (saved list) normalize into. The reorder
 * endpoint already returns this two-axis shape; the saved-list shape is mapped
 * onto it. Prices are in the order/list currency's major units.
 */
export interface ReorderLine {
  status: ReorderLineStatus;
  variant_id: string | null;
  title: string;
  thumbnail?: string | null;
  quantity: number;
  /** present on `available` / `repriced` */
  offer_id?: string;
  /** current unit price - on `available` */
  unit_price?: number;
  /** original -> current unit price - on `repriced` */
  old_price?: number;
  new_price?: number;
  /** why the line could not be re-added - on `unavailable` */
  reason?: UnavailableReason;
  /**
   * The resolved supplier for this line, when the source resolves per line. The
   * variant-keyed stock reorder (F-stock, api#152) sets this so each row can show
   * "from <supplier>" - a tracked variant is supplier-agnostic and can fan out
   * across sellers. `undefined` for order/saved-list sources, which carry a
   * single seller at the header level instead; `null` on an unavailable line.
   */
  seller?: { id: string; name: string } | null;
}

/** `available` + `repriced` lines are the ones that get added to the cart. */
export function isAddable(line: ReorderLine): boolean {
  return line.status === "available" || line.status === "repriced";
}

/**
 * The unit price a line will actually add at: the new price when repriced, else
 * the current unit price (0 on an `unavailable` line, which is never added).
 * Lives here so a surface computing a line or seller subtotal consumes the
 * repriced-vs-available rule instead of re-deriving it.
 */
export function lineUnitPrice(line: ReorderLine): number {
  return line.status === "repriced"
    ? (line.new_price ?? 0)
    : (line.unit_price ?? 0);
}

/** A buyer's per-line pick + quantity override, keyed by `variant_id`. */
export interface LineSelection {
  variant_id: string;
  quantity: number;
}

/**
 * Builds the `selections` payload the confirm mutations send: one entry per
 * addable line the buyer left checked, with its (possibly edited) quantity.
 * Lines without a `variant_id` can never be selected (there is nothing to key
 * the backend selection on) and are silently excluded, same as an unavailable
 * line - the review dialog never renders a checkbox for either.
 */
export function buildSelections(
  lines: ReorderLine[],
  selected: Set<string>,
  quantities: Record<string, number>
): LineSelection[] {
  return lines
    .filter(
      (line): line is ReorderLine & { variant_id: string } =>
        isAddable(line) &&
        line.variant_id !== null &&
        selected.has(line.variant_id)
    )
    .map((line) => ({
      variant_id: line.variant_id,
      quantity: quantities[line.variant_id] ?? line.quantity
    }));
}

/** Tally of the buyer's picks, for the reorder-decision analytics events. */
export interface ReorderSelectionCounts {
  /** Addable lines left checked. */
  selectedCount: number;
  /** Addable lines the buyer unchecked. */
  deselectedCount: number;
  /** Selected lines whose quantity was edited away from the line's original. */
  overriddenCount: number;
}

/**
 * Summarizes the buyer's selection against the resolved diff - the "why"
 * behind a reorder decision (ADR-0003): how much of what was offered did they
 * actually take, and how much did they adjust.
 */
export function summarizeSelection(
  lines: ReorderLine[],
  selected: Set<string>,
  quantities: Record<string, number>
): ReorderSelectionCounts {
  let selectedCount = 0;
  let deselectedCount = 0;
  let overriddenCount = 0;

  for (const line of lines) {
    if (!isAddable(line) || line.variant_id === null) {
      continue;
    }
    if (!selected.has(line.variant_id)) {
      deselectedCount += 1;
      continue;
    }
    selectedCount += 1;
    const quantity = quantities[line.variant_id] ?? line.quantity;
    if (quantity !== line.quantity) {
      overriddenCount += 1;
    }
  }

  return { selectedCount, deselectedCount, overriddenCount };
}

/** Per-status tally shared by the cart summary and the analytics events. */
export interface ReorderLineCounts {
  /** `available` + `repriced` - the lines that land in the cart. */
  added: number;
  /** `repriced` lines among those added. */
  repriced: number;
  /** lines skipped (`unavailable` / `discontinued`). */
  unavailable: number;
}

/**
 * Tally a resolved diff into the full triad. Every reorder-decision analytics
 * event reads the fields it needs from this one result: F2 uses all three, F3
 * ignores `repriced` (a saved list stores no baseline price, so it is always 0).
 */
export function summarize(lines: ReorderLine[]): ReorderLineCounts {
  let added = 0;
  let repriced = 0;
  let unavailable = 0;
  for (const line of lines) {
    if (line.status === "repriced") {
      repriced += 1;
      added += 1;
    } else if (line.status === "available") {
      added += 1;
    } else {
      unavailable += 1;
    }
  }
  return { added, repriced, unavailable };
}

const REASON_LABEL: Record<UnavailableReason, string> = {
  discontinued: "No longer available",
  out_of_stock: "Out of stock",
  inactive: "Unavailable"
};

/** Human label for an unavailable line's reason, with a safe generic fallback. */
export function reasonLabel(reason: UnavailableReason | undefined): string {
  return (reason && REASON_LABEL[reason]) || "Unavailable";
}

/**
 * Normalize the reorder endpoint's lines. `/store/reorder` already returns the
 * canonical two-axis shape, so this is a boundary pass-through that re-brands
 * the wire type as the domain `ReorderLine`. It is the one place to adapt if
 * the reorder wire shape ever diverges from the domain shape.
 */
export function normalizeReorderLines(
  lines: StoreReorderLine[]
): ReorderLine[] {
  return lines;
}

/**
 * Normalize the variant-keyed stock-reorder endpoint's lines (api#152). Like
 * `/store/reorder`, it already returns the canonical changed-line shape - plus a
 * per-line `seller` - so this is another boundary pass-through, kept distinct so
 * the stock-reorder wire shape has its own adaptation point if it ever diverges.
 */
export function normalizeStockReorderLines(
  lines: StoreStockReorderLine[]
): ReorderLine[] {
  return lines;
}

type QuotedCartLine = SalonStockerHttpTypes.StoreSellerCartLineItem;

/**
 * Normalize cart lines carrying a **quote diff** into `ReorderLine` (KAN-39).
 *
 * A concierge cart is a third changed-line source: an operator quoted a price
 * hours ago, `refreshStaleCartPricesWorkflow` may have moved it since, and the
 * salon needs the same "what changed" reading a reorder gives them. The server
 * already classifies each line into the shared two-axis vocabulary
 * (`available` / `repriced` / `unavailable` + reason), so this is another
 * boundary adapter - the diff rules stay owned by this module, and the tally,
 * labels and banner logic are reused unchanged rather than re-derived.
 *
 * Lines with no `quote_diff` are dropped: they were added by the salon
 * themselves and have no quote to have moved from, so counting them as
 * "unchanged" would inflate the banner's denominator.
 */
export function normalizeQuotedCartLines(
  items: QuotedCartLine[]
): ReorderLine[] {
  return items
    .filter(
      (
        item
      ): item is QuotedCartLine & {
        quote_diff: NonNullable<QuotedCartLine["quote_diff"]>;
      } => Boolean(item.quote_diff)
    )
    .map((item) => ({
      status: item.quote_diff.status,
      variant_id: item.variant_id ?? null,
      title: item.title,
      thumbnail: item.thumbnail ?? null,
      quantity: item.quantity,
      unit_price: item.quote_diff.new_price,
      old_price: item.quote_diff.old_price,
      new_price: item.quote_diff.new_price,
      reason: item.quote_diff.reason
    }));
}

type ResolvedItem = SalonStockerHttpTypes.StoreResolvedSavedListItem;

/**
 * Normalize saved-list resolved items into `ReorderLine`. A saved list carries
 * its classification on the single `availability_status` axis (F3 reuses the
 * reorder enum with `discontinued` folded in) and stores no baseline price, so
 * it never yields `repriced`. The unavailable axis is split back into the
 * canonical status + reason: `discontinued` -> no live offer exists;
 * `unavailable` -> an offer exists but cannot be added right now (`inactive`).
 */
export function normalizeResolvedItems(items: ResolvedItem[]): ReorderLine[] {
  return items.map((item) => {
    const status: ReorderLineStatus =
      item.availability_status === "available"
        ? "available"
        : item.availability_status === "repriced"
          ? "repriced"
          : "unavailable";

    const reason: UnavailableReason | undefined =
      item.availability_status === "discontinued"
        ? "discontinued"
        : status === "unavailable"
          ? "inactive"
          : undefined;

    return {
      status,
      variant_id: item.variant_id,
      title: item.title,
      thumbnail: item.thumbnail ?? null,
      quantity: item.quantity,
      offer_id: item.offer_id,
      unit_price: item.unit_price,
      reason
    };
  });
}
